import { useCallback, useEffect, useRef, useState } from 'react';

import { validateUsername } from '@/api/user';

import { useTranslate } from '@/hooks/useTranslate';

import { UsernameValidationResult } from '@/types/user';

import { getSuggestedUsernames } from '@/utils/user';

const USERNAME_VALIDATION_DEBOUNCE_MS = 300;

export function useValidateUsername(birthday: string, phoneNumber: string) {
  const { t } = useTranslate();
  const [suggestedUsernames, setSuggestedUsernames] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const lastValidatedValue = useRef<UsernameValidationResult | null>(null);
  const validationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortController = useRef<AbortController | null>(null);
  const resolvePendingValidation = useRef<
    ((result: true | string) => void) | null
  >(null);
  const errorMessage = t(
    'createNewAccount.basicInfo.form.username.error.availability',
  );

  const generateUsernames = useCallback(
    async (username: string, signal: AbortSignal) => {
      const suggestions = getSuggestedUsernames(
        username,
        birthday,
        phoneNumber,
      );
      const validatedUsernames = await validateUsername(suggestions, signal);

      return validatedUsernames
        .filter(({ isValid }) => isValid)
        .slice(0, 3)
        .map(({ username }) => username);
    },
    [birthday, phoneNumber],
  );

  const validateUsernameInput = useCallback(
    async (value: string, controller: AbortController) => {
      if (value === lastValidatedValue.current?.username) {
        return lastValidatedValue.current?.isValid ? true : errorMessage;
      }

      try {
        setIsValidating(true);
        const result = await validateUsername([value], controller.signal);
        lastValidatedValue.current = result[0];

        if (result[0]?.isValid) {
          setSuggestedUsernames([]);
          return true;
        }

        const validUsernames = await generateUsernames(
          value,
          controller.signal,
        );

        if (validUsernames.length === 3) {
          setSuggestedUsernames(validUsernames);
          return errorMessage;
        }

        const moreValidUsernames = await generateUsernames(
          value.slice(0, -1),
          controller.signal,
        );

        setSuggestedUsernames(
          [...validUsernames, ...moreValidUsernames].slice(0, 3),
        );

        return errorMessage;
      } catch {
        if (controller.signal.aborted) {
          return true;
        }

        setSuggestedUsernames([]);
        return true;
      } finally {
        if (abortController.current === controller) {
          abortController.current = null;
          setIsValidating(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generateUsernames],
  );

  const cancelPendingValidation = useCallback(() => {
    if (validationTimer.current) {
      clearTimeout(validationTimer.current);
      validationTimer.current = null;
    }

    resolvePendingValidation.current?.(true);
    resolvePendingValidation.current = null;
    abortController.current?.abort();
    abortController.current = null;
  }, []);

  const validateUsernameDebounced = useCallback(
    (value: string = '') => {
      cancelPendingValidation();
      setIsValidating(false);

      return new Promise<true | string>(resolve => {
        resolvePendingValidation.current = resolve;
        validationTimer.current = setTimeout(async () => {
          validationTimer.current = null;
          resolvePendingValidation.current = null;
          const controller = new AbortController();
          abortController.current = controller;
          resolve(await validateUsernameInput(value, controller));
        }, USERNAME_VALIDATION_DEBOUNCE_MS);
      });
    },
    [cancelPendingValidation, validateUsernameInput],
  );

  const resetValidation = () => {
    cancelPendingValidation();
    setIsValidating(false);
    setSuggestedUsernames([]);
  };

  useEffect(() => {
    cancelPendingValidation();
    setIsValidating(false);
    setSuggestedUsernames([]);
    lastValidatedValue.current = null;
  }, [birthday, phoneNumber, cancelPendingValidation]);

  return {
    validateUsername: validateUsernameDebounced,
    suggestedUsernames,
    resetValidation,
    isValidating,
  };
}
